/**
 * Site Types Page - Microsoft 365 Style
 * Manage different types of security sites
 */

import React, { useEffect, useState } from 'react';
import {
  makeStyles,
  Button,
  Input,
  Label,
  Text,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Textarea,
  Badge,
} from '@fluentui/react-components';
import { EditRegular, DeleteRegular, BuildingRegular, SearchRegular } from '@fluentui/react-icons';
import { ContentCard, TableCommandBar } from '../../../components/layout';
import { TablePagination, SlidePanel } from '../../../components/common';
import { usePagination } from '../../../hooks/usePagination';
import { ApiClient, newToast } from '../../../configurations/index.config';
import { getErrorMessage } from '../../../types';

const useStyles = makeStyles({
  pageHeader: {
    marginBottom: '24px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
    display: 'block',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    display: 'block',
  },
  searchBar: {
    marginBottom: '16px',
    maxWidth: '400px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #e1dfdd',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
  },
  tr: {
    borderBottom: '1px solid #e1dfdd',
    ':hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#323130',
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginBottom: '16px',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
  },
});

interface SiteType {
  id: string;
  name: string;
  description?: string;
}

export const SiteTypesFluentUI: React.FC = () => {
  const styles = useStyles();
  const [siteTypes, setSiteTypes] = useState<SiteType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSiteType, setSelectedSiteType] = useState<SiteType | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  useEffect(() => {
    fetchSiteTypes();
  }, []);

  const fetchSiteTypes = async () => {
    setLoading(true);
    try {
      const response = await ApiClient.get('/get-site-types');
      if (response.data.status === 'success') {
        setSiteTypes(response.data.data || []);
      } else {
        newToast('error', response.data?.message || 'Failed to fetch site types');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const handleOpenDialog = (siteType?: SiteType) => {
    if (siteType) {
      setSelectedSiteType(siteType);
      setFormData({
        name: siteType.name,
        description: siteType.description || '',
      });
    } else {
      setSelectedSiteType(null);
      setFormData({ name: '', description: '' });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      newToast('warning', 'Please enter site type name');
      return;
    }

    try {
      const payload = {
        id: selectedSiteType?.id || '',
        name: formData.name,
        description: formData.description,
        department_id: null,
      };

      const response = await ApiClient.post('/add-update-site-type', payload);
      if (response.data.status === 'success') {
        newToast('success', response.data.message || 'Site type saved successfully');
        fetchSiteTypes();
        setDialogOpen(false);
      } else {
        newToast('error', response.data?.message || 'Failed to save site type');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  const handleDelete = async () => {
    if (!selectedSiteType) {
      return;
    }

    try {
      const response = await ApiClient.post('/delete-site-type', {
        id: selectedSiteType.id,
      });

      if (response.data.status === 'success') {
        newToast('success', 'Site type deleted successfully');
        fetchSiteTypes();
      } else {
        newToast('error', response.data?.message || 'Failed to delete site type');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setDeleteDialogOpen(false);
    setSelectedSiteType(null);
  };

  const filteredSiteTypes = siteTypes.filter(siteType => {
    const query = searchQuery.toLowerCase();
    return (
      siteType.name?.toLowerCase().includes(query) ||
      siteType.description?.toLowerCase().includes(query)
    );
  });

  // Pagination
  const { currentPage, itemsPerPage, paginatedData, totalItems, setCurrentPage, setItemsPerPage } =
    usePagination({
      data: filteredSiteTypes,
      initialPage: 1,
      initialItemsPerPage: 10,
    });

  return (
    <div>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <Text className={styles.pageTitle}>Manage Site Types</Text>
        <Text className={styles.pageSubtitle}>
          {loading ? 'Loading...' : `${siteTypes.length} site types`}
        </Text>
      </div>

      {/* Command Bar */}
      <TableCommandBar onAdd={() => handleOpenDialog()} addLabel="Add Site Type" />

      {/* Search Bar */}
      <div className={styles.searchBar}>
        <Input
          placeholder="Search site types..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          contentBefore={<SearchRegular />}
          size="medium"
        />
      </div>

      {/* Site Types Table */}
      <ContentCard noPadding>
        {loading ? (
          <div className={styles.emptyState}>
            <Text>Loading site types...</Text>
          </div>
        ) : filteredSiteTypes.length === 0 ? (
          <div className={styles.emptyState}>
            <BuildingRegular style={{ fontSize: '48px', marginBottom: '12px' }} />
            <Text size={500} weight="semibold" style={{ display: 'block' }}>
              No site types found
            </Text>
            <Text style={{ marginTop: '8px', display: 'block' }}>
              {searchQuery
                ? 'Try adjusting your search'
                : 'Click "Add Site Type" to create your first site type'}
            </Text>
          </div>
        ) : (
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th className={styles.th}>Type Name</th>
                <th className={styles.th}>Description</th>
                <th className={styles.th} style={{ width: '150px' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map(siteType => (
                <tr key={siteType.id} className={styles.tr}>
                  <td className={styles.td}>
                    <Badge appearance="filled" color="informative">
                      {siteType.name}
                    </Badge>
                  </td>
                  <td className={styles.td}>{siteType.description || '—'}</td>
                  <td className={styles.td}>
                    <div className={styles.actionButtons}>
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<EditRegular />}
                        onClick={() => handleOpenDialog(siteType)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<DeleteRegular />}
                        onClick={() => {
                          setSelectedSiteType(siteType);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && filteredSiteTypes.length > 0 && (
          <TablePagination
            totalItems={totalItems}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        )}
      </ContentCard>

      {/* Add/Edit Slide Panel */}
      <SlidePanel
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title={selectedSiteType ? 'Edit Site Type' : 'Add Site Type'}
        subtitle={selectedSiteType ? 'Update site type details' : 'Create a new site type'}
        width="33%"
        footer={
          <>
            <Button appearance="secondary" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button appearance="primary" onClick={handleSave}>
              {selectedSiteType ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <div className={styles.formField}>
          <Label required>Type Name</Label>
          <Input
            value={formData.name}
            onChange={e => setFormData({ ...formData, name: e.target.value })}
            placeholder="Enter site type name"
          />
        </div>

        <div className={styles.formField}>
          <Label>Description</Label>
          <Textarea
            value={formData.description}
            onChange={e => setFormData({ ...formData, description: e.target.value })}
            placeholder="Enter description (optional)"
            rows={3}
          />
        </div>
      </SlidePanel>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(e, data) => setDeleteDialogOpen(data.open)}>
        <DialogSurface
          style={{
            maxWidth: '400px',
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <DialogBody>
            <DialogTitle>Delete Site Type</DialogTitle>
            <DialogContent>
              Are you sure you want to delete <strong>{selectedSiteType?.name}</strong>? This action
              cannot be undone.
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={handleDelete}>
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default SiteTypesFluentUI;
